import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { KeycloakSessionService } from '../auth/keycloak-session.service';
import { TokenStorageService } from '../auth/token-storage.service';
import { buildErrorDetail, EnrichedHttpError, extractBodyMessage, extractUserDetail } from '../../shared/utils/error.utils';
import { isFirstPartyApi } from './first-party-api';
import { expireSession, SESSION_EXPIRED_STATUS_TEXT } from './session-expired';

const FORCE_REFRESH_TIMEOUT_MS = 10000;

/** Blob types that carry text a person can read: the JSON error body of the backend, a plain text or HTML page. */
const TEXT_BLOB_TYPE = /^(?:text\/|application\/(?:[\w.-]+\+)?json\b)/i;

@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {

  constructor(
    private readonly router: Router,
    private readonly keycloakSession: KeycloakSessionService,
    private readonly tokenStorage: TokenStorageService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((failure: HttpErrorResponse) => this.decodeBlobBody(failure).pipe(
        mergeMap(error => this.rethrow(request, error))
      ))
    );
  }

  private rethrow(request: HttpRequest<unknown>, error: HttpErrorResponse): Observable<never> {
    const enriched = this.enrich(request, error);

    // Only our own backends speak for the user's session; the synthetic 401 raised by AuthInterceptor is already handled.
    if (error.status === 401 && isFirstPartyApi(request.url) && error.statusText !== SESSION_EXPIRED_STATUS_TEXT) {
      return from(this.handleUnauthorized(request.url)).pipe(
        mergeMap(() => throwError(() => enriched))
      );
    }

    return throwError(() => enriched);
  }

  /**
   * A request made with responseType 'blob' (the reservations PDF) delivers the JSON error of the backend as a Blob,
   * which no message extractor can read. Text-like bodies are decoded here, once, so that every caller gets the same
   * userMessage / errorDetail as for any other request; binary bodies are left alone.
   */
  private decodeBlobBody(error: HttpErrorResponse): Observable<HttpErrorResponse> {
    const body = error.error;
    if (!(body instanceof Blob) || !TEXT_BLOB_TYPE.test(body.type)) {
      return of(error);
    }

    return from(body.text()).pipe(
      map(text => new HttpErrorResponse({
        error: parseTextBody(text),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined
      })),
      // The request failed anyway: if its body cannot be read the caller still gets the original HTTP error.
      catchError(readError => {
        console.warn('[ErrorsInterceptor] Unable to read the body of an error response:', readError);
        return of(error);
      })
    );
  }

  /**
   * The Java backend also answers 401 for business errors ("operation not allowed"), so a 401 alone does not
   * end the session. A token that looks valid locally may still be rejected by the server (revoked session,
   * clock skew), so the server is asked: a forced refresh that succeeds proves the session is alive (business
   * error), one that fails with the refresh token gone ends it, any other failure keeps the session.
   */
  private async handleUnauthorized(url: string): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      // The keycloak-js refresh request has no timeout of its own: without this cap a hung refresh would
      // leave the original 401 pending forever, and so the caller's spinner/save button.
      const refreshTimeout = new Promise<'unavailable'>(resolve => {
        timeoutId = setTimeout(() => resolve('unavailable'), FORCE_REFRESH_TIMEOUT_MS);
      });
      const outcome = await Promise.race([
        this.keycloakSession.forceRefresh(`401 response: ${url}`),
        refreshTimeout
      ]);
      if (outcome === 'refreshed') {
        console.debug('[ErrorsInterceptor] 401 but the session refreshed fine — business error, session kept. URL:', url);
        return;
      }

      if (outcome === 'unavailable') {
        console.debug('[ErrorsInterceptor] 401 but the token refresh may recover — session kept. URL:', url);
        return;
      }

      console.debug('[ErrorsInterceptor] 401 Unauthorized and the session cannot be refreshed — clearing session, redirecting to login. URL:', url);
      expireSession(this.keycloakSession, this.router);
    } catch (error) {
      console.warn('[ErrorsInterceptor] Unable to check the session after a 401:', error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private enrich(request: HttpRequest<unknown>, error: HttpErrorResponse): EnrichedHttpError {
    const user = this.tokenStorage.getUser();
    const errorDetail = buildErrorDetail(request, error, user);

    const enriched = Object.assign(Object.create(Object.getPrototypeOf(error)), error) as EnrichedHttpError;
    // Set only when the backend body provides a message, so callers' contextual fallback text can still win.
    enriched.userMessage = extractBodyMessage(error);
    enriched.userMessageDetail = extractUserDetail(error);
    enriched.errorDetail = errorDetail;
    return enriched;
  }
}

/** The parsed JSON object, or the raw text when it is not a JSON object, or null for an empty body. */
function parseTextBody(text: string): unknown {
  if (!text.trim()) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(text);
    return parsed !== null && typeof parsed === 'object' ? parsed : text;
  } catch {
    return text;
  }
}
